import {Observable, ReplaySubject, Scheduler, Subject} from "rxjs";
import {IHttpService} from "angular";
import {UserConfig} from "../../types/UserConfig";
import {RecentPortfolio, sortAscending} from "./models/RecentPortfolio";
import {ArrayUtil} from "../../js/util/ArrayUtil";
import {Optional} from "../../js/util/Optional";

const MaxRecentPortfolios = 4;

interface AccountSummary {
    id: number;
    accountName: string;
}

export class RecentPortfolioService {

    public static id = "recentPortfolioService";

    private readonly star$ = new Subject<AccountSummary>();
    private readonly unstar$ = new Subject<AccountSummary>();
    private readonly open$ = new Subject<AccountSummary>();
    private readonly portfolios$ = new ReplaySubject<Array<RecentPortfolio>>(1);

    /** @ngInject */
    public constructor(private $http: IHttpService, private userConfig: UserConfig) {
        this.init();
    }

    private init() {
        this.initFromUserConfig();

        this.star$
            .observeOn(Scheduler.async)
            .withLatestFrom(this.portfolios)
            .flatMap(([star, portfolios]) => this.addFavourite(star, portfolios))
            .subscribe((portfolios) => this.portfolios$.next(portfolios));

        this.unstar$
            .observeOn(Scheduler.async)
            .withLatestFrom(this.portfolios)
            .flatMap(([unstar, portfolios]) => this.removeFavourite(unstar, portfolios))
            .subscribe((portfolios) => this.portfolios$.next(portfolios));

        this.open$
            .observeOn(Scheduler.async)
            .withLatestFrom(this.portfolios)
            .map(([open, portfolios]) => this.updateRecent(open, portfolios))
            .subscribe((portfolios) => this.portfolios$.next(portfolios));
    }

    private initFromUserConfig() {
        const currentFavourites = Optional.of(this.userConfig)
                                          .map((uc) => uc.favourites)
                                          .orElse([]);
        const portfolios = currentFavourites.map((f) => {
            return {
                id: f.id,
                name: f.name,
                favourite: true
            };
        });
        this.portfolios$.next(portfolios);
    }

    public get portfolios(): Observable<Array<RecentPortfolio>> {
        return this.portfolios$
                   .observeOn(Scheduler.async);
    }

    public get recent(): Observable<Array<RecentPortfolio>> {
        return this.portfolios.map((portfolios) => portfolios.filter((p) => !p.favourite));
    }

    public get favourites(): Observable<Array<RecentPortfolio>> {
        return this.portfolios
                   .map((portfolios) => portfolios.filter((p) => p.favourite))
                   .map(p => p.sort(sortAscending()));
    }

    public star(portfolio: AccountSummary) {
        this.star$.next(portfolio);
    }

    private async addFavourite(add: AccountSummary,
                               portfolios: Array<RecentPortfolio>): Promise<Array<RecentPortfolio>> {
        const id = add.id;
        const url = `/tradefloor/api/portfolio/${id}/favourite`;
        await this.$http.put(url, null);
        const newEntry: RecentPortfolio = {
            id: add.id,
            name: add.accountName,
            favourite: true
        };

        const copy = ArrayUtil.clone(portfolios);
        copy.push(newEntry);
        return copy;
    }

    public unstar(portfolio: AccountSummary) {
        this.unstar$.next(portfolio);
    }

    public openPortfolio(portfolio: AccountSummary) {
        this.open$.next(portfolio);
    }

    private async removeFavourite(remove: AccountSummary,
                                  portfolios: Array<RecentPortfolio>): Promise<Array<RecentPortfolio>> {
        const id = remove.id;
        const url = `/tradefloor/api/portfolio/${id}/favourite`;
        await this.$http.delete(url);

        return portfolios.filter((p) => p.id !== remove.id);
    }

    private updateRecent(recent: AccountSummary, portfolios: Array<RecentPortfolio>): Array<RecentPortfolio> {
        const exists = portfolios.some((p) => p.id === recent.id);
        if (exists) {
            return portfolios;
        }
        const copy = ArrayUtil.clone(portfolios);
        const newEntry: RecentPortfolio = {
            id: recent.id,
            name: recent.accountName,
            favourite: false
        };
        copy.unshift(newEntry);

        const [favourites, history] = ArrayUtil.split(copy, (rp) => rp.favourite);

        if (history.length > MaxRecentPortfolios) {
            const limited = history.slice(0, MaxRecentPortfolios - 1);
            return favourites.concat(limited);
        } else {
            return favourites.concat(history);
        }
    }
}
